import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';

// Sørg for at binde modal til din app root for tilgængelighed.
// Find dit root element (typisk 'root' i public/index.html) og brug det her.
Modal.setAppElement('#root');

const initialFormStateCensor = {
  fornavn: '', efternavn: '', adresse: '', postnummer: '', forstad: '',
  telefon: '', email: '', 
  beskikket_indfodsret: false,
  beskikket_medborgerskab: false,
  beskikket_pd1: false,
  beskikket_pd2: false,
  beskikket_pd3: false,
  beskikket_sp: false,
  arbejds_status: '', prioritet: '', sprogcenter_id: '', kommentar: ''
};

function CensorerListe() {
  const [censorer, setCensorer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // States for den ENE modal
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [editingCensorId, setEditingCensorId] = useState(null); // Beholder denne til at vide, hvilken censor vi arbejder med
  const [formState, setFormState] = useState(initialFormStateCensor);
  const [modalError, setModalError] = useState(null);
  
  // NY STATE: Styrer om modalen er i view- eller edit-mode
  const [isEditing, setIsEditing] = useState(false); 
  
  const [sprogcentreOptions, setSprogcentreOptions] = useState([]);
  const [loadingSprogcentreOptions, setLoadingSprogcentreOptions] = useState(true);

  const API_BASE_URL_CENSORER = `${process.env.REACT_APP_API_URL}/api/censorer`;
  const API_BASE_URL_SPROGCENTRE = `${process.env.REACT_APP_API_URL}/api/sprogcentre`;

  const getBeskikkelserLabel = (censorLike) => {
    if (!censorLike) return '-';
    const labels = [];
    if (censorLike.beskikket_pd1) labels.push('PD1');
    if (censorLike.beskikket_pd2) labels.push('PD2');
    if (censorLike.beskikket_pd3) labels.push('PD3');
    if (censorLike.beskikket_sp) labels.push('SP');
    if (censorLike.beskikket_indfodsret) labels.push('Indfødsret');
    if (censorLike.beskikket_medborgerskab) labels.push('Medborgerskab');
    return labels.length ? labels.join(', ') : '-';
  };

  const openModal = (censor, startInEditMode = false) => {
    setModalError(null);
    setIsEditing(startInEditMode); // Sæt tilstanden (view eller edit)

    // Hvis vi tilføjer en ny, er der ingen censor, og vi er altid i edit mode
    if (!censor) {
      setEditingCensorId(null);
      setFormState(initialFormStateCensor);
      setIsEditing(true);
    } else {
      // Hvis vi ser/redigerer en eksisterende, udfyld data
      setEditingCensorId(censor.id);
      setFormState({
        fornavn: censor.fornavn || '',
        efternavn: censor.efternavn || '',
        adresse: censor.adresse || '',
        postnummer: censor.postnummer || '',
        forstad: censor.forstad || '',
        telefon: censor.telefon || '',
        email: censor.email || '',
        beskikket_indfodsret: censor.beskikket_indfodsret || false,
        beskikket_medborgerskab: censor.beskikket_medborgerskab || false,
        beskikket_pd1: censor.beskikket_pd1 || false,
        beskikket_pd2: censor.beskikket_pd2 || false,
        beskikket_pd3: censor.beskikket_pd3 || false,
        beskikket_sp: censor.beskikket_sp || false,
        arbejds_status: censor.arbejds_status || '',
        prioritet: censor.prioritet || '',
        sprogcenter_id: censor.sprogcenter_id ? censor.sprogcenter_id.toString() : '',
        kommentar: censor.kommentar || ''
      });
    }
    setModalIsOpen(true); // Åbn modalen
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setModalError(null);
    setEditingCensorId(null);
    setIsEditing(false); // Nulstil altid tilstanden
  };
  
  const fetchData = async () => {
    setLoading(true); setLoadingSprogcentreOptions(true); setError(null);
    try {
        const [censorerRes, sprogcentreRes] = await Promise.all([
            fetch(API_BASE_URL_CENSORER, { credentials: 'include' }),
            fetch(API_BASE_URL_SPROGCENTRE, { credentials: 'include' })
        ]);
        if (!censorerRes.ok) throw new Error(`Censorer: ${censorerRes.statusText || censorerRes.status}`);
        if (!sprogcentreRes.ok) throw new Error(`Sprogcentre: ${sprogcentreRes.statusText || sprogcentreRes.status}`);
        
        const censorerData = await censorerRes.json();
        const sprogcentreData = await sprogcentreRes.json();

        // --- DEN NYE SORTERINGSLOGIK ER HER ---
        // Vi sorterer dataen baseret på fornavn, derefter efternavn.
        const sortedCensorer = censorerData.sort((a, b) => {
          // Sammenlign efternavn
          const firstNameComparison = (a.fornavn || '').localeCompare(b.fornavn || '');
          if (firstNameComparison !== 0) {
            return firstNameComparison;
          }
          // Hvis efternavne er ens, sammenlign efternavn
          return (a.efternavn || '').localeCompare(b.efternavn || '');
        });

        setCensorer(sortedCensorer); // Sæt den sorterede data i state
        setSprogcentreOptions(sprogcentreData);
    } catch (err) {
        console.error("Fejl ved hentning af data:", err); setError(err.message);
    } finally {
        setLoading(false); setLoadingSprogcentreOptions(false);
    }
};

  useEffect(() => {
    fetchData();
  }, []);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState(prevState => ({ ...prevState, [name]: type === 'checkbox' ? checked : value }));
  };

  const resetForm = () => {
    setFormState(initialFormStateCensor);
  };

  const handleCensorSubmit = async (event) => {
    event.preventDefault(); setModalError(null);
    if (!formState.fornavn.trim() || !formState.efternavn.trim()) {
      setModalError('Fornavn og Efternavn er påkrævede felter.'); return;
    }

    const prioritetValueToStore = formState.prioritet && formState.prioritet.trim() !== "" ? formState.prioritet.trim() : null;
    let sprogcenterIdValue = formState.sprogcenter_id ? parseInt(formState.sprogcenter_id, 10) : null;
    if (isNaN(sprogcenterIdValue)) sprogcenterIdValue = null;

    const censorData = {
        ...formState,
        prioritet: prioritetValueToStore,
        sprogcenter_id: sprogcenterIdValue,
    };

    const submitButton = event.target.querySelector('button[type="submit"]');
    if(submitButton) submitButton.disabled = true;

    try {
      let response;
      const fetchOptions = {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(censorData),
        credentials: 'include' // Rettelse #3 (gælder for både POST og PUT)
      };

      if (editingCensorId) {
        response = await fetch(`${API_BASE_URL_CENSORER}/${editingCensorId}`, {
          method: 'PUT',
          ...fetchOptions
        });
      } else {
        response = await fetch(API_BASE_URL_CENSORER, {
          method: 'POST',
          ...fetchOptions
        });
      }
      if (!response.ok) { const errorText = await response.text(); throw new Error(errorText || `Serverfejl: ${response.status}`);}
      
      closeModal();
      fetchData(); // Gen-hent alt data for at sikre konsistens
      
    } catch (error) {
      console.error(editingCensorId ? "Fejl ved opdatering af censor:" : "Fejl ved gemning af censor:", error);
      setModalError(`Kunne ikke ${editingCensorId ? 'opdatere' : 'gemme'} censor: ${error.message}`);
    } finally {
      if(submitButton) submitButton.disabled = false;
    }
  };

  const handleDeleteCensor = async (idToDelete) => {
    if (!window.confirm(`Sikker på du vil slette censor med ID ${idToDelete}?`)) return;
    setError(null); setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL_CENSORER}/${idToDelete}`, { 
        method: 'DELETE',
        credentials: 'include' // Rettelse #4
      });

      if (!response.ok && response.status !== 204) { const errorText = await response.text(); throw new Error(errorText || `Serverfejl: ${response.status}`);}
      
      fetchData(); // Gen-hent alt data for at sikre konsistens

    } catch (error) {
      console.error("Fejl ved sletning af censor:", error); setError(`Kunne ikke slette censor: ${error.message}`);
    } finally {
      setLoading(false); 
    }
  };

  return (
    <div>
      <div className="add-new-button-container">
        <button onClick={() => openModal()} className="toggle-form-button primary-action" disabled={loadingSprogcentreOptions}>
          {loadingSprogcentreOptions ? 'Henter data...' : 'Tilføj Ny Censor'}
        </button>
      </div>

      <h2 className="page-main-header">Eksisterende Censorer</h2>

      {error && !modalIsOpen && <p className="error-message" style={{ textAlign: 'center' }}>{error}</p>}
      
      {/* DEN ENE, SAMLEDE MODAL FOR OPRET, VIS OG REDIGER */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Censor Detaljer"
        className="ReactModal__Content"
        overlayClassName="ReactModal__Overlay"
      >
        <div> {/* Overordnet container for alt indhold i modalen */}
          <form onSubmit={handleCensorSubmit} className="modal-form">
              <h3>{editingCensorId ? (isEditing ? `Rediger Censor (ID: ${editingCensorId})` : `Detaljer for ${formState.fornavn} ${formState.efternavn}`) : 'Tilføj Ny Censor'}</h3>
              {modalError && <p className="error-message" style={{marginBottom: '15px'}}>{modalError}</p>}
              
              {/* Navn */}
              <div className="form-row">
                <div className="form-field">
                  <label>Fornavn:</label>
                  {isEditing ? (
                    <input type="text" name="fornavn" value={formState.fornavn} onChange={handleInputChange} required />
                  ) : (
                    <p className="form-static-text">{formState.fornavn || '-'}</p>
                  )}
                </div>
                <div className="form-field">
                  <label>Efternavn:</label>
                  {isEditing ? (
                    <input type="text" name="efternavn" value={formState.efternavn} onChange={handleInputChange} required />
                  ) : (
                    <p className="form-static-text">{formState.efternavn || '-'}</p>
                  )}
                </div>
              </div>

              {/* Adresse */}
              <div className="form-field">
                <label>Adresse:</label>
                {isEditing ? (
                    <div className="form-row">
                        <div className="form-field"><input type="text" name="adresse" value={formState.adresse} onChange={handleInputChange} placeholder="Gade og nr."/></div>
                        <div className="form-field"><input type="text" name="postnummer" value={formState.postnummer} onChange={handleInputChange} placeholder="Postnr."/></div>
                        <div className="form-field"><input type="text" name="forstad" value={formState.forstad} onChange={handleInputChange} placeholder="By"/></div>
                    </div>
                ) : (
                  <p className="form-static-text">{`${formState.adresse || ''}, ${formState.postnummer || ''} ${formState.forstad || ''}`.replace(/^, /, '').replace(/, $/, '').trim() || '-'}</p>
                )}
              </div>

              {/* Kontakt */}
              <div className="form-row">
                <div className="form-field">
                  <label>Telefon:</label>
                  {isEditing ? (
                    <input type="tel" name="telefon" value={formState.telefon} onChange={handleInputChange} />
                  ) : (
                    <p className="form-static-text">{formState.telefon || '-'}</p>
                  )}
                </div>
                <div className="form-field">
                  <label>Email:</label>
                  {isEditing ? (
                    <input type="email" name="email" value={formState.email} onChange={handleInputChange} />
                  ) : (
                    <p className="form-static-text">{formState.email || '-'}</p>
                  )}
                </div>
              </div>

              {/* Beskikkelser */}
              <div className="form-field">
                <label>Beskikkelser:</label>
                {!isEditing && (
                  <p className="form-static-text">{getBeskikkelserLabel(formState)}</p>
                )}
              </div>

              <div className="form-row">
                  <div className={`form-field-check ${!isEditing ? 'read-only-check' : ''}`}>
                      <input type="checkbox" name="beskikket_pd1" checked={formState.beskikket_pd1} onChange={handleInputChange} disabled={!isEditing} />
                      <label>PD1</label>
                  </div>
                  <div className={`form-field-check ${!isEditing ? 'read-only-check' : ''}`}>
                      <input type="checkbox" name="beskikket_pd2" checked={formState.beskikket_pd2} onChange={handleInputChange} disabled={!isEditing} />
                      <label>PD2</label>
                  </div>
                  <div className={`form-field-check ${!isEditing ? 'read-only-check' : ''}`}>
                      <input type="checkbox" name="beskikket_pd3" checked={formState.beskikket_pd3} onChange={handleInputChange} disabled={!isEditing} />
                      <label>PD3</label>
                  </div>
                  <div className={`form-field-check ${!isEditing ? 'read-only-check' : ''}`}>
                      <input type="checkbox" name="beskikket_sp" checked={formState.beskikket_sp} onChange={handleInputChange} disabled={!isEditing} />
                      <label>SP</label>
                  </div>
                  <div className={`form-field-check ${!isEditing ? 'read-only-check' : ''}`}>
                      <input type="checkbox" name="beskikket_indfodsret" checked={formState.beskikket_indfodsret} onChange={handleInputChange} disabled={!isEditing} />
                      <label>Beskikket Indfødsret</label>
                  </div>
                  <div className={`form-field-check ${!isEditing ? 'read-only-check' : ''}`}>
                      <input type="checkbox" name="beskikket_medborgerskab" checked={formState.beskikket_medborgerskab} onChange={handleInputChange} disabled={!isEditing} />
                      <label>Beskikket Medborgerskab</label>
                  </div>
              </div>
              
              {/* Status, Prio, Sprogcenter */}
              <div className="form-row">
                <div className="form-field">
                  <label>Arbejdsstatus:</label>
                  {isEditing ? (
                    <select name="arbejds_status" value={formState.arbejds_status} onChange={handleInputChange}>
                      <option value="">-- Vælg Status --</option><option value="Tilgængelig">Tilgængelig</option><option value="Inaktiv">Inaktiv</option><option value="Syg">Syg</option><option value="Barsel">Barsel</option>
                    </select>
                  ) : (
                    <p className="form-static-text">{formState.arbejds_status || '-'}</p>
                  )}
                </div>
                <div className="form-field">
                  <label>Prioritet:</label>
                   {isEditing ? (
                    <select name="prioritet" value={formState.prioritet} onChange={handleInputChange}>
                      <option value="">-- Vælg Prioritet --</option><option value="Almindelig">Almindelig</option><option value="Forcensur">Forcensur</option><option value="Obligatorisk fravalgt">Obligatorisk fravalgt</option>
                    </select>
                   ) : (
                    <p className="form-static-text">{formState.prioritet || '-'}</p>
                   )}
                </div>
                <div className="form-field">
                  <label>Tilknyttet Sprogcenter:</label>
                  {isEditing ? (
                    <select name="sprogcenter_id" value={formState.sprogcenter_id} onChange={handleInputChange} disabled={loadingSprogcentreOptions}>
                      <option value="">-- Vælg Sprogcenter --</option>
                      {loadingSprogcentreOptions ? <option disabled>Henter...</option> : sprogcentreOptions.map(sc => (<option key={sc.id} value={sc.id}>{sc.navn}</option>))}
                    </select>
                  ) : (
                    <p className="form-static-text">{sprogcentreOptions.find(sc => sc.id.toString() === formState.sprogcenter_id)?.navn || 'Ikke tilknyttet'}</p>
                  )}
                </div>
              </div>

              {/* Kommentar */}
              <div className="form-field">
                <label>Kommentar:</label>
                {isEditing ? (
                  <textarea name="kommentar" value={formState.kommentar} onChange={handleInputChange} rows="3"></textarea>
                ) : (
                  <p className="form-static-text" style={{ whiteSpace: 'pre-wrap', minHeight: '80px' }}>{formState.kommentar || '-'}</p>
                )}
              </div>
              
              {/* Viser KUN submit-knapperne, HVIS vi er i edit-mode */}
              {isEditing && (
                <div className="modal-actions">
                  <button type="submit">
                    {editingCensorId ? 'Gem Ændringer' : 'Opret Censor'}
                  </button>
                  {editingCensorId && <button type="button" onClick={() => setIsEditing(false)} className="secondary-button">Fortryd</button>}
                  <button type="button" onClick={closeModal} className="cancel-button">Annuller</button>
                </div>
              )}
          </form>

          {/* Knapperne til View Mode ligger nu UDEN FOR formen */}
          {!isEditing && (
              <div className="modal-actions">
                <button type="button" onClick={() => setIsEditing(true)} className="primary-action">
                  Rediger
                </button>
                <button type="button" onClick={closeModal} className="cancel-button">
                  Luk
                </button>
              </div>
          )}
        </div>
      </Modal>


      {loading && <p className="info-message">Henter censorer...</p>}
      {!loading && censorer.length === 0 && !error && ( <p className="info-message">Ingen censorer fundet.</p> )}
      {!loading && censorer.length > 0 && (
          <div className="table-container">
          <table className="censor-table">
          <thead>
                <tr>
                  <th>Navn</th>
                  <th>Telefon</th>
                  <th>Email</th>
                  <th>Beskikkelser</th>
                  <th className="action-cell">Handlinger</th>
                </tr>
              </thead>

              

              <tbody>
                {censorer.map(censor => (
                  // Klik på rækken åbner i "view mode" (startInEditMode = false)
                  <tr key={censor.id} onClick={() => openModal(censor, false)} className="clickable-row" title="Se detaljer">
                    <td><strong>{censor.fornavn} {censor.efternavn}</strong></td>
                    <td>{censor.telefon || '-'}</td>
                    <td>{censor.email || '-'}</td>
                    <td>{getBeskikkelserLabel(censor)}</td>
                    
                    <td className="action-cell" onClick={(e) => e.stopPropagation()}>
                      {/* Klik på rediger-ikonet åbner i "edit mode" (startInEditMode = true) */}
                      <button onClick={() => openModal(censor, true)} className="edit-button icon-button" title={`Rediger ${censor.fornavn}`} style={{ marginRight: '8px' }}>
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18px" height="18px"><path d="M21.731 2.269a2.625 2.625 0 00-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 000-3.712zM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 00-1.32 2.214l-.8 2.685a.75.75 0 00.933.933l2.685-.8a5.25 5.25 0 002.214-1.32l8.4-8.4z" /><path d="M5.25 5.25a3 3 0 00-3 3v10.5a3 3 0 003 3h10.5a3 3 0 003-3V13.5a.75.75 0 00-1.5 0v5.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5V8.25a1.5 1.5 0 011.5-1.5h5.25a.75.75 0 000-1.5H5.25z" /></svg>
                          </button>
                      <button onClick={() => handleDeleteCensor(censor.id)} className="delete-button icon-button" title={`Slet ${censor.fornavn} ${censor.efternavn}`}>
                               <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18px" height="18px" className="delete-icon"><path fillRule="evenodd" d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.9h1.368c1.603 0 2.816 1.336 2.816 2.9zm-1.452.721a.75.75 0 01.216-.534l.018-.018a.75.75 0 01.706-.216L17.5 5.25a.75.75 0 01.216.534l-.018.018a.75.75 0 01-.706.216l-1.281-.214a.75.75 0 01-.216-.534z" clipRule="evenodd" /></svg>
                          </button>
                    </td>
                  </tr>
                ))}
              </tbody>
          </table>
          </div>
      )}
    </div>
  );
}

export default CensorerListe;